export const FILTERS = [
    { id: 'all', label: '全部' },
    { id: 'completed', label: '完成' },
    { id: 'draft', label: '草稿' },
];

export const STATUS_META = {
    draft: { label: '待确认', className: 'bg-purple-50 text-purple-700' },
    queued: { label: '排队中', className: 'bg-amber-50 text-amber-700' },
    running: { label: '处理中', className: 'bg-blue-50 text-blue-700' },
    succeeded: { label: '已完成', className: 'bg-emerald-50 text-emerald-700' },
    partial_failed: { label: '部分失败', className: 'bg-orange-50 text-orange-700' },
    failed: { label: '失败', className: 'bg-red-50 text-red-700' },
};

export const MOCK_TASKS = [];

export const isMockTask = (task) => String(task?.task_id || '').startsWith('mock_task_');

export const getStatusMeta = (status) => STATUS_META[status] || { label: status || '未知', className: 'bg-gray-100 text-gray-600' };

export const formatTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
};

/** 智能相对时间（列表视图）：今天 19:09 / 昨天 21:22 / 02/25 19:09 / 2025/12/31 */
export const formatSmartTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    const now = new Date();
    const hm = date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false });
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) return `今天 ${hm}`;
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return `昨天 ${hm}`;
    const sameYear = date.getFullYear() === now.getFullYear();
    const md = `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
    if (sameYear) return `${md} ${hm}`;
    return `${date.getFullYear()}/${md} ${hm}`;
};

const _detectPageType = (text, idx, total) => {
    const firstLine = (text.split('\n')[0] || '').trim();
    const m = firstLine.match(/^\[(.+?)\]/);
    if (m) {
        const marker = m[1];
        if (marker.includes('封面')) return '封面';
        if (marker.includes('总结')) return '总结';
        if (marker.includes('内容')) return '内容';
    }
    // 无标记时按位置推断
    if (idx === 1) return '封面';
    if (idx === total) return '总结';
    return '内容';
};

export const parseOutlinePages = (outlineText = '') => {
    const normalized = String(outlineText || '').trim();
    if (!normalized) return [];

    const hasPageMarker = /<\/?page>/i.test(normalized);
    let pageParts = [];

    if (hasPageMarker) {
        const matchedBlocks = [...normalized.matchAll(/<page>([\s\S]*?)<\/page>/gi)]
            .map((match) => String(match[1] || '').trim())
            .filter(Boolean);

        pageParts = matchedBlocks.length > 0
            ? matchedBlocks
            : normalized
                .split(/<page>/i)
                .map((item) => item.replace(/<\/page>/gi, '').trim())
                .filter(Boolean);
    } else {
        pageParts = normalized
            .split('\n')
            .map((item) => item.trim())
            .filter(Boolean);
    }

    const capped = pageParts.slice(0, 18);
    const total = capped.length;
    return capped.map((content, index) => ({
        order: index + 1,
        type: _detectPageType(content, index + 1, total),
        content,
    }));
};

export const normalizeTaskPages = (task) => {
    if (!task) return [];

    const parsedPages = parseOutlinePages(task.outline);
    const images = Array.isArray(task.images) ? task.images : [];
    const imageMap = new Map();

    images.forEach((img, index) => {
        const order = Number(img?.page_number || index + 1);
        imageMap.set(order, img);
    });

    const mergedMap = new Map();
    parsedPages.forEach((page) => {
        mergedMap.set(page.order, {
            ...page,
            image: imageMap.get(page.order) || null,
        });
    });

    imageMap.forEach((image, order) => {
        if (mergedMap.has(order)) return;
        mergedMap.set(order, {
            order,
            type: order === 1 ? '封面' : `第 ${order} 页`,
            content: image?.prompt || '',
            image,
        });
    });

    return [...mergedMap.values()].sort((a, b) => a.order - b.order);
};

export const getTaskTitle = (task) => {
    const firstTitle = Array.isArray(task?.titles) ? task.titles[0] : '';
    return firstTitle || task?.topic || task?.task_id || '未命名任务';
};

export const getTaskCover = (task) => {
    const first = Array.isArray(task?.images) ? task.images[0] : null;
    if (!first) return '';
    return typeof first === 'string' ? first : (first.url || '');
};

export const guessPlatformByTask = (task) => {
    const skillName = String(task?.skill_name || '').toLowerCase();
    if (skillName.includes('wechat') || skillName.includes('weixin')) return 'wechat';
    return 'xiaohongshu';
};

/** 将 skill_name 映射为用户友好的中文名称 */
export const getSkillDisplayName = (skillName) => {
    if (!skillName) return '';
    const SKILL_MAP = {
        'xiaohongshu_亲子': '亲子内容',
        'xiaohongshu_洗发水': '洗发水营销',
        'xiaohongshu_default': '通用笔记',
        'xiaohongshu_seeding': '种草文',
        'xiaohongshu_review': '测评文',
        'xiaohongshu_story': '故事文',
        'wechat_marketing': '营销文',
        'wechat_tutorial': '教程文',
    };
    return SKILL_MAP[skillName] || skillName;
};

export const sanitizeFilename = (name) => String(name || '')
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/\s+/g, '_')
    .slice(0, 48);

export const downloadTextFile = (text, fileName) => {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = `${fileName}.txt`;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
};
